import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, CircularProgress, List, Alert, Snackbar } from '@mui/material';
import PageTitle from '../../../components/PageTitle/PageTitle';
import DirectoryNode from '../../../components/DirectoryNode/DirectoryNode';
import { getArchiveDirectory } from '../../../services/documentService';
import { buildTreeNode } from '../../../utilities/treeUtils';
import DocumentViewModal from '../../../components/DocumentViewModal';
import { useTranslation } from 'react-i18next';

const BasicTraining = () => {
    const ROOT_PATH = 'Training Materials';
    const [directoryData, setDirectoryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState({});
    const [loadingChildren, setLoadingChildren] = useState({});
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState({ path: null, name: null });

    // Normalize folder display names
    const normalizeFolderName = (name = '') => {
        const normalized = name.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');

        if (normalized === 'bcbt' || normalized === 'cbt') {
            return 'Basic Computer Based Training';
        }

        if (normalized === 'cvbt' || normalized === 'vbt') {
            return 'Basic Video Based Training';
        }

        return name;
    };

    // Check if folder is a B-CBT (Basic Computer Based Training) folder
    const isCBTFolder = (folderPath) => {
        const folderName = folderPath.split('/').pop().toLowerCase();
        const normalized = folderName.replace(/\s+/g, '').replace(/-/g, '');
        return normalized === 'bcbt';
    };

    // Find index.html in a folder's children (recursively search through HTML folder)
    const findIndexHtml = async (folderPath) => {
        try {
            
            const data = await fetchDirectoryContent(folderPath);

            // Search through directories for HTML folder
            if (data.directories) {
                for (const dir of data.directories) {
                    if (dir.name.toLowerCase() === 'html') {
                        const htmlFolderPath = dir.path;
                        

                        // Load HTML folder contents
                        const htmlData = await fetchDirectoryContent(htmlFolderPath);

                        // Search for index.html in files
                        if (htmlData.files) {
                            const indexFile = htmlData.files.find(f =>
                                f.name.toLowerCase().includes('index.html')
                            );

                            if (indexFile) {
                                
                                return indexFile;
                            }
                        }

                        // Search in subdirectories of HTML folder
                        if (htmlData.directories) {
                            for (const subDir of htmlData.directories) {
                                const subDirData = await fetchDirectoryContent(subDir.path);
                                if (subDirData.files) {
                                    const indexFile = subDirData.files.find(f =>
                                        f.name.toLowerCase().includes('index.html')
                                    );

                                    if (indexFile) {
                                        
                                        return indexFile;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Direct search in files (fallback)
            if (data.files) {
                const indexFile = data.files.find(f =>
                    f.name.toLowerCase().includes('index.html')
                );
                if (indexFile) {
                    
                    return indexFile;
                }
            }

            
            return null;
        } catch (err) {
            console.error('Error finding index.html:', err);
            return null;
        }
    };

    // Fetch folder content from backend
    const fetchDirectoryContent = async (path) => {
        try {
            

            const normalizedPath = !path || path === "/" ? "" : path.replace(/^\/+|\/+$/g, "");
            

            const response = await getArchiveDirectory(normalizedPath);
            const data = response?.data || response || { directories: [], files: [] };

            

            return data;
        } catch (err) {
            console.error('Fetch error:', err);
            throw err;
        }
    };

    // Open file in modal (ALL files including HTML)
    const handleFileClick = (filePath) => {
        try {
            if (!filePath) return;

            // Extract file name
            const fileName = filePath.split('/').pop();

            
            

            setSelectedFile({ path: filePath, name: fileName });
            setModalOpen(true);
        } catch (err) {
            setError(`${t('BASIC_TRAINING.ERROR_OPEN_FILE')}: ${err.message}`);
        }
    };
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const handleFolderToggle = useCallback(
        async (folderPath) => {
            

            // Check if this is a B-CBT folder
            if (isCBTFolder(folderPath)) {
                
                setLoadingChildren(prev => ({ ...prev, [folderPath]: true }));

                try {
                    const indexFile = await findIndexHtml(folderPath);

                    if (indexFile && indexFile.path) {
                        
                        handleFileClick(indexFile.path);
                    } else {
                        setError(t('BASIC_TRAINING.ERROR_INDEX_NOT_FOUND'));
                    }
                } catch (err) {
                    console.error('Error opening B-CBT folder:', err);
                    setError(`${t('BASIC_TRAINING.ERROR_OPEN_TRAINING')}: ${err.message}`);
                } finally {
                    setLoadingChildren(prev => ({ ...prev, [folderPath]: false }));
                }
                return;
            }

            // Normal folder toggle behavior
            const isExpanded = expandedFolders[folderPath];
            

            if (!isExpanded) {
                setLoadingChildren(prev => ({ ...prev, [folderPath]: true }));

                try {
                    
                    const data = await fetchDirectoryContent(folderPath);
                    const rawNode = buildTreeNode(data, folderPath);

                    const folderNode = {
                        ...rawNode,
                        children: rawNode.children?.map(child => ({
                            ...child,
                            name: normalizeFolderName(child.name)
                        }))
                    };

                    

                    setDirectoryData(prev => {
                        const patch = (nodes = []) =>
                            nodes.map(n => {
                                if (n.path === folderPath) {
                                    
                                    return { ...n, children: folderNode.children || [] };
                                }
                                if (Array.isArray(n.children)) {
                                    return { ...n, children: patch(n.children) };
                                }
                                return n;
                            });
                        return patch(prev);
                    });
                } catch (err) {
                    console.error(`Failed:`, err);
                    setError(`${t('BASIC_TRAINING.ERROR_LOAD_FOLDER')}: ${err.message}`);
                } finally {
                    setLoadingChildren(prev => ({ ...prev, [folderPath]: false }));
                }
            }

            setExpandedFolders(prev => ({ ...prev, [folderPath]: !isExpanded }));
        },
        [expandedFolders]
    );

    // Load root on mount
    useEffect(() => {
        const loadRoot = async () => {
            
            setLoading(true);

            try {
                const rootData = await fetchDirectoryContent(ROOT_PATH);
                const rootNode = buildTreeNode(rootData, ROOT_PATH, 'Basic Training Materials');

                const applyRename = (node) => ({
                    ...node,
                    name: normalizeFolderName(node.name),
                    children: Array.isArray(node.children)
                        ? node.children.map(applyRename)
                        : node.children
                });

                
                

                setDirectoryData([applyRename(rootNode)]);
                setExpandedFolders({ [ROOT_PATH]: true });

                
            } catch (err) {
                console.error(`Root load failed:`, err);
                setError(`${t('BASIC_TRAINING.ERROR_LOAD_CONTENT')}: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        loadRoot();
    }, []);

    if (loading) {
        return (
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 400,
                    maxWidth: 1400,
                    mx: 'auto',
                    width: '100%'
                }}
            >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>
                    {t('BASIC_TRAINING.LOADING')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                pt: 0,
                pb: 4,
                maxWidth: 1400,
                mx: 'auto',
                width: '100%'
            }}
        >
            <PageTitle title={t('BASIC_TRAINING.TITLE')} />

            {error && (
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Snackbar>
            )}

            <Paper
                sx={{
                    p: 2,
                    maxHeight: 600,
                    overflow: 'auto'
                }}
            >
                {directoryData.length > 0 ? (
                    <List dense disablePadding>
                        {directoryData.map(rootNode => (
                            <DirectoryNode
                                key={rootNode.path}
                                node={rootNode}
                                expandedFolders={expandedFolders}
                                onFolderToggle={handleFolderToggle}
                                onFileClick={handleFileClick}
                                loadingChildren={loadingChildren}
                                isCBTFolder={isCBTFolder}
                            />
                        ))}
                    </List>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            {t('BASIC_TRAINING.NO_MATERIALS')}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* File Preview Modal - For ALL file types including HTML */}
            <DocumentViewModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                filePath={selectedFile.path}
                fileName={selectedFile.name}
            />
        </Box>
    );
};

export default BasicTraining;